// src/utils/taxRules.js
// Frontend version of tax rules (UI metadata only)
// This is a simplified version for frontend use - full rules are in backend/taxRules/

const taxRules = {
  DE: {
    ui: {
      locale: 'de-DE',
      categories: {
        disposal: {
          en: 'Disposal transactions (German Income Tax Act: §23 EStG)',
          de: 'Veräußerungsgeschäfte (§23 EStG)',
          native: 'Veräußerungsgeschäfte (§23 EStG)'
        },
        other: {
          en: 'Other income (German Income Tax Act: §22 No. 3 EStG)',
          de: 'Sonstige Einkünfte (§22 Nr. 3 EStG)',
          native: 'Sonstige Einkünfte (§22 Nr. 3 EStG)'
        }
      },
      thresholds: (_year) => ({
        mode: 'separate',
        disposal: {
          amount: 1000,
          label: {
            en: 'Exemption threshold: tax-free only if total §23 gain is less than €1,000',
            de: 'Freigrenze: steuerfrei nur bei einem Gesamtgewinn von weniger als 1.000 €'
          }
        },
        other: {
          amount: 256,
          label: {
            en: 'Exemption threshold: tax-free only if §22 No. 3 income is less than €256',
            de: 'Freigrenze: steuerfrei nur bei Einkünften von weniger als 256 €'
          }
        }
      }),
      notes: {
        en: 'Germany uses separate thresholds for disposals (€1,000) and other income (€256).',
        de: 'Deutschland verwendet separate Freigrenzen für Veräußerungen (1.000 €) und sonstige Einkünfte (256 €).',
        native: 'Deutschland verwendet separate Freigrenzen für Veräußerungen (1.000 €) und sonstige Einkünfte (256 €).'
      }
    }
  },
  IT: {
    ui: {
      locale: 'it-IT',
      categories: {
        disposal: {
          en: 'Crypto capital gains/losses (Italy: Quadro RT)',
          de: 'Krypto-Plusvalenzen/Minusvalenzen (Italien: Quadro RT)',
          native: 'Plusvalenze e minusvalenze da cripto-attività – Quadro RT'
        },
        other: {
          en: 'Crypto proceeds (Staking/Airdrops/Rewards) (Italy: Quadro RT)',
          de: 'Krypto-Erträge (Staking/Airdrops/Rewards) (Italien: Quadro RT)',
          native: 'Altri proventi da cripto-attività (staking/ricompense) – Quadro RT'
        }
      },
      thresholds: (year) => {
        // Kombinierte Netto-Schwelle: <= 2024: 2.000 EUR, ab 2025: 0 EUR
        const threshold = year >= 2025 ? 0 : 2000;
        if (threshold === 0) {
          return { mode: 'none' };
        }
        return {
          mode: 'combined',
          amount: threshold,
          label: {
            en: `Net threshold: €${threshold.toLocaleString()}`,
            de: `Netto-Schwelle: ${threshold.toLocaleString()} €`
          }
        };
      },
      notes: {
        en: 'In Italy, both capital gains/losses and staking/rewards proceeds are reported in Quadro RT ("redditi diversi") under the current crypto regime (since 2023). Quadro RW (monitoring) may also apply for portfolio reporting.',
        de: 'In Italien werden sowohl Kapitalgewinne/-Verluste als auch Staking/Rewards-Erträge im Quadro RT („redditi diversi") gemeldet (aktuelles Regime ab 2023). Quadro RW (Monitoring) kann zusätzlich für Portfolio-Reporting relevant sein.',
        native: 'In Italia, sia plusvalenze/minusvalenze che proventi da staking/ricompense sono dichiarati nel Quadro RT (redditi diversi) secondo il regime attuale (dal 2023). Quadro RW (monitoraggio) può essere rilevante per il reporting del portafoglio.'
      }
    }
  },
  GR: {
    ui: {
      locale: 'el-GR',
      categories: {
        disposal: {
          en: 'Crypto gains/losses (Greece)',
          de: 'Krypto-Gewinne/-Verluste (Griechenland)',
          native: 'Κέρδη/ζημίες από διάθεση κρυπτοστοιχείων'
        },
        other: {
          en: 'Other crypto income/proceeds (Greece)',
          de: 'Sonstige Krypto-Erträge (Griechenland)',
          native: 'Λοιπά έσοδα/απολαβές από κρυπτοστοιχεία (staking κ.λπ.)'
        }
      },
      thresholds: () => ({ mode: 'none' }),
      notes: {
        en: 'Greece has no fully standardized crypto tax framework (as of 2025). Labels are informational.',
        de: 'Griechenland hat (Stand 2025) kein vollständig standardisiertes Krypto-Steuerframework. Bezeichnungen sind informativ.',
        native: 'Η φορολογία κρυπτοστοιχείων δεν είναι πλήρως τυποποιημένη (έως το 2025). Οι ενδείξεις είναι ενημερωτικές.'
      }
    }
  },
  US: {
    ui: {
      locale: 'en-US',
      categories: {
        disposal: {
          en: 'Capital gains/losses (Form 8949, Schedule D)',
          de: 'Kapitalgewinne/-Verluste (Form 8949, Schedule D)',
          native: 'Capital gains/losses (Form 8949, Schedule D)'
        },
        other: {
          en: 'Ordinary income (Form 1040, Schedule 1)',
          de: 'Gewöhnliches Einkommen (Form 1040, Schedule 1)',
          native: 'Ordinary income (Form 1040, Schedule 1)'
        }
      },
      thresholds: (_year) => ({
        mode: 'none'
      }),
      notes: {
        en: 'US reporting uses Form 8949 (capital gains) and Schedule 1 (ordinary income).',
        de: 'In den USA erfolgt die Meldung über Form 8949 (Kapitalgewinne) und Schedule 1 (gewöhnliches Einkommen).',
        native: 'US reporting uses Form 8949 (capital gains) and Schedule 1 (ordinary income).'
      }
    }
  },
  UK: {
    ui: {
      locale: 'en-GB',
      categories: {
        disposal: {
          en: 'Capital gains/losses (UK: SA108 / Cryptoasset Self Assessment)',
          de: 'Kapitalgewinne/-verluste (UK: SA108 / Cryptoasset Self Assessment)',
          native: 'Capital gains/losses (UK: SA108 / Cryptoasset Self Assessment)'
        },
        other: {
          en: 'Miscellaneous income (staking/rewards/airdrops)',
          de: 'Miscellaneous income (Staking/Rewards/Airdrops)',
          native: 'Miscellaneous income (staking/rewards/airdrops)'
        }
      },
      thresholds: (year) => {
        const y = Number(year);
        let aea = 3000;
        if (y <= 2022) aea = 12300;
        else if (y === 2023) aea = 6000;
        return {
          mode: 'combined',
          amount: aea,
          label: {
            en: `Annual Exempt Amount: £${aea.toLocaleString('en-GB')}`,
            de: `Annual Exempt Amount: £${aea.toLocaleString('en-GB')}`
          },
          miscAllowance: 1000,
          miscAllowanceLabel: {
            en: 'Miscellaneous income allowance: £1,000',
            de: 'Miscellaneous income allowance: £1.000'
          }
        };
      },
      notes: {
        en: 'UK uses Section 104 pooling for fungible tokens (same-day / 30-day matching). NFTs are not pooled. Report via Self Assessment / SA108.',
        de: 'UK nutzt Section-104-Pooling für fungible Token (Same-Day / 30-Day). NFTs werden nicht gepoolt. Meldung über Self Assessment / SA108.',
        native: 'UK uses Section 104 pooling for fungible tokens. NFTs are not pooled.'
      }
    }
  }
};

export function getTaxRules(countryCode) {
  const code = (countryCode || 'DE').toUpperCase();
  if (code === 'GB') return taxRules.UK;
  return taxRules[code] || taxRules.DE;
}

/**
 * Get category text for a specific country and category
 */
export function getCategoryText({ countryCode, categoryKey, language = 'en', showNative = true }) {
  const rules = getTaxRules(countryCode);
  const ui = rules?.ui;
  const cat = ui?.categories?.[categoryKey];

  // Fallbacks
  const primary =
    cat?.[language] ||
    cat?.en ||
    (categoryKey === 'disposal' ? 'Disposals' : 'Other income');

  const secondary = showNative ? (cat?.native || null) : null;

  const note = ui?.notes?.[language] || ui?.notes?.en || null;

  return { primary, secondary, note };
}

/**
 * Get threshold UI information for a specific country and year
 */
export function getThresholdUi({ countryCode, year, language = 'en' }) {
  const rules = getTaxRules(countryCode);
  const t = rules?.ui?.thresholds;
  const thresholds = typeof t === 'function' ? t(year) : t;

  if (!thresholds || thresholds.mode === 'none') {
    return { mode: 'none' };
  }

  // separate: { disposal: {amount,label}, other: {amount,label} }
  // combined: { amount, label }
  if (thresholds.mode === 'separate') {
    const d = thresholds.disposal?.label?.[language] || thresholds.disposal?.label?.en || null;
    const o = thresholds.other?.label?.[language] || thresholds.other?.label?.en || null;
    return {
      ...thresholds,
      disposalLabel: d,
      otherLabel: o
    };
  }

  if (thresholds.mode === 'combined') {
    const l = thresholds.label?.[language] || thresholds.label?.en || null;
    return {
      ...thresholds,
      combinedLabel: l
    };
  }

  return { mode: 'none' };
}

